import React from 'react';
import { AppWindow, Download, KeyRound, Link2, MapPin, MonitorPlay } from 'lucide-react';
import { cn } from '../lib/utils';
import { API_BASE_URL } from '../apiBase';
import {
  buildAppSnippet,
  fillAppPlaceholders,
  partnerAppLogoSrc,
  WEB_PLAYER_OPTION_ID,
} from '../lib/partnerApps';

function Logo({ app, size = 'md' }) {
  const src = partnerAppLogoSrc(app?.logo_url, API_BASE_URL);
  const dim = size === 'sm' ? 'h-7 w-7' : size === 'lg' ? 'h-14 w-14' : 'h-10 w-10';
  const icon = size === 'sm' ? 14 : size === 'lg' ? 22 : 18;
  const isWeb = String(app?.id) === WEB_PLAYER_OPTION_ID;
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border/70 bg-muted/70',
        dim,
      )}
    >
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
      ) : isWeb ? (
        <MonitorPlay className="text-primary" size={icon} strokeWidth={1.9} />
      ) : (
        <AppWindow className="text-muted-foreground" size={icon} strokeWidth={1.9} />
      )}
    </span>
  );
}

/**
 * Chip compacto — listas (Clientes / Testes).
 * `labeled` = faixa do modal de sucesso (mobile).
 */
export function PartnerAppBadge({ app, className, emptyLabel = null, labeled = false }) {
  if (!app) {
    if (!emptyLabel) return null;
    return (
      <span className={cn('inline-flex items-center text-[11px] text-muted-foreground', className)}>
        {emptyLabel}
      </span>
    );
  }
  if (labeled) {
    return (
      <span
        className={cn(
          'inline-flex w-full max-w-full items-center gap-2 rounded-xl border border-border/80 bg-card/80 py-2 pl-1.5 pr-2.5',
          className,
        )}
        title={app.devices_label || (app.devices || []).join(', ') || app.name}
      >
        <Logo app={app} size="md" />
        <span className="min-w-0 flex-1">
          <span className="block text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
            Aplicativo do cliente
          </span>
          <span className="block truncate text-[13px] font-semibold text-foreground">{app.name}</span>
        </span>
        {app.access_code ? (
          <span className="shrink-0 rounded-md bg-muted/70 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
            #{app.access_code}
          </span>
        ) : null}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-md border border-border/80 bg-muted/40 py-0.5 pl-0.5 pr-2',
        className,
      )}
      title={app.devices_label || (app.devices || []).join(', ') || app.name}
    >
      <Logo app={app} size="sm" />
      <span className="min-w-0 truncate text-[11.5px] font-semibold text-foreground">{app.name}</span>
      {app.access_code ? (
        <span className="shrink-0 font-mono text-[10px] text-muted-foreground">#{app.access_code}</span>
      ) : null}
    </span>
  );
}

function Field({ icon: Icon, label, value, mono }) {
  if (!value) return null;
  return (
    <div className="pa-identity-field rounded-lg border border-border/70 bg-background/60 px-3 py-2">
      <div className="pa-identity-label flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
        {Icon ? <Icon className="h-3 w-3 shrink-0 opacity-80" /> : null}
        {label}
      </div>
      <div className={cn('mt-1 text-[13px] font-medium text-foreground break-words', mono && 'font-mono')}>
        {value}
      </div>
    </div>
  );
}

/**
 * Painel estruturado para modais Office (não usa markdown do WhatsApp).
 */
export function PartnerAppPanel({
  app,
  className,
  vars = {},
  showWaPreview = false,
  title = 'Aplicativo do cliente',
}) {
  if (!app) return null;
  const devices = Array.isArray(app.devices)
    ? app.devices.filter(Boolean)
    : String(app.devices_label || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
  const where = String(app.where_to_find || '').trim();
  const waBlock = showWaPreview
    ? fillAppPlaceholders(buildAppSnippet(app, { detail: true }), vars)
    : '';

  return (
    <section
      className={cn(
        'pa-identity-panel overflow-hidden rounded-xl border border-border/80 bg-card/60 shadow-sm',
        className,
      )}
    >
      <div className="flex items-start gap-3 border-b border-border/60 bg-muted/25 px-3.5 py-3 sm:px-4">
        <Logo app={app} size="lg" />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
            {title}
          </p>
          <h3 className="pa-identity-title mt-0.5 truncate text-base font-semibold text-foreground">{app.name}</h3>
          {devices.length ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {devices.slice(0, 8).map((d) => (
                <span
                  key={d}
                  className="rounded-md border border-border/70 bg-background/70 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                >
                  {d}
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">Dispositivos conforme orientação abaixo</p>
          )}
        </div>
      </div>

      <div className="space-y-3 px-3.5 py-3 sm:px-4">
        <div className="grid gap-2 sm:grid-cols-2">
          <Field icon={KeyRound} label="Código de acesso" value={app.access_code} mono />
          <Field icon={Download} label="Código Downloader" value={app.downloader_code} mono />
        </div>
        <Field icon={Link2} label="Link de download" value={app.download_url} />
        {app.downloader_url !== app.download_url && <Field icon={Download} label="URL Downloader" value={app.downloader_url} />}
        {where ? (
          <div className="pa-identity-field rounded-lg border border-border/70 bg-background/60 px-3 py-2.5">
            <div className="pa-identity-label flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0 opacity-80" />
              Onde encontrar
            </div>
            <pre className="mt-1.5 whitespace-pre-wrap font-sans text-[12.5px] leading-relaxed text-foreground/90">
              {where}
            </pre>
          </div>
        ) : null}
        {showWaPreview && waBlock ? (
          <div className="rounded-lg border border-dashed border-border/80 bg-muted/20 px-3 py-2.5">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Pré-visualização (mensagem)
            </p>
            <pre className="whitespace-pre-wrap font-sans text-[12px] leading-relaxed text-foreground/85">
              {waBlock}
            </pre>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default PartnerAppBadge;
